// BuergschaftTile — T2.16c. Aggregat "Konzern haftet mit X €" auf
// Cockpit Hero-Reihe. Klick → Drawer mit Liste pro creditor+debtor.
//
// R1: Total summiert nur Buergschaften wo Holding-Firma Creditor ist
// (Backend rechnet das, total_creditor_eur). InfraX-debtor-Side ist
// als Stamm sichtbar (Schnittstelle), aber NICHT in der Sum.

import { useState } from "react";

import {
  BuergschaftAggregateResponse,
  BuergschaftItem,
  formatEUR,
} from "../../lib/api";

interface Props {
  data?: BuergschaftAggregateResponse;
  loading?: boolean;
}

export default function BuergschaftTile({ data, loading }: Props) {
  const [drawer, setDrawer] = useState(false);
  const total = data?.total_creditor_eur ?? "0";
  const n = data?.n_creditor ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => data && data.items.length > 0 && setDrawer(true)}
        disabled={!data || data.items.length === 0}
        className="rounded-xl border border-amber-500/40 bg-amber-500/[0.05] px-3 py-2 min-h-14 flex flex-col justify-center text-left hover:bg-amber-500/[0.08] active:bg-amber-500/[0.12] disabled:opacity-50"
      >
        <div className="text-[10px] uppercase tracking-widest text-amber-300 truncate">
          Bürgschaften (Konzern haftet)
        </div>
        <div className="mt-0.5 text-sm sm:text-base font-bold tabular-nums text-amber-100 truncate">
          {loading ? "…" : formatEUR(total)}
        </div>
        <div className="text-[10px] text-amber-300/70 truncate">
          {n > 0 ? `${n} aktive · klick → Liste` : "keine aktiven"}
        </div>
      </button>
      {drawer && data && (
        <BuergschaftDrawer items={data.items} onClose={() => setDrawer(false)} />
      )}
    </>
  );
}

function BuergschaftDrawer({
  items,
  onClose,
}: {
  items: BuergschaftItem[];
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Bürgschaften"
    >
      <div
        className="w-full sm:max-w-2xl bg-slate-900 border-t sm:border border-slate-700 sm:rounded-2xl p-5 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-baseline justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-100">
            Bürgschaften (Konzern haftet mit)
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="h-11 w-11 inline-flex items-center justify-center rounded-lg text-slate-300 hover:bg-slate-800"
          >
            ✕
          </button>
        </header>
        <p className="text-[11px] text-slate-400 mb-3">
          R1-konform: Schnittstellen-Bürgschaft X-Holding↔InfraX zeigt nur
          Stamm (Betrag, Laufzeit) — kein InfraX-Cashflow-Bezug.
        </p>
        <ul className="space-y-2">
          {items.map((b) => {
            // S5-P2: Severity je Laufzeit-Countdown
            const daysLeft = b.laufzeit_bis
              ? Math.floor(
                  (new Date(b.laufzeit_bis).getTime() - Date.now()) /
                    86_400_000,
                )
              : null;
            const sevClass =
              daysLeft != null && daysLeft <= 30
                ? "border-pink-500/50"
                : daysLeft != null && daysLeft <= 90
                  ? "border-amber-500/40"
                  : "border-slate-700/60";
            return (
              <li
                key={b.id}
                className={`rounded-lg border ${sevClass} bg-slate-950 p-3`}
              >
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="text-sm font-semibold text-slate-100 truncate">
                    {b.name}
                  </span>
                  <span className="tabular-nums font-bold text-amber-200">
                    {formatEUR(b.betrag_eur)}
                  </span>
                </div>
                <div className="text-[11px] text-slate-400">
                  {b.creditor_display}{" "}
                  <span className="text-slate-500">→</span> {b.debtor_display}
                  {b.laufzeit_bis && (
                    <span className="ml-2 text-slate-500">
                      bis {b.laufzeit_bis}
                      {daysLeft != null && daysLeft >= 0 && (
                        <span
                          className={
                            daysLeft <= 30
                              ? "ml-1 text-pink-300"
                              : daysLeft <= 90
                                ? "ml-1 text-amber-300"
                                : "ml-1 text-slate-500"
                          }
                        >
                          ({daysLeft}T)
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {b.sicherungsobjekt && (
                  <div className="text-[10px] text-slate-500 mt-1">
                    {b.sicherungsobjekt}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
